<template>
  <div class="bg-gray-21 px-3">
    <div class="d-flex align-items-center mb-4 ml-n3">
      <circle-icon
        shadow
        size="31px"
        icon-size="20px"
        color="#fff"
        icon-color="#fff"
      >
        <template #icon>
          <img src="@/assets/icons/folder-grid.svg" />
        </template>
      </circle-icon>
      <span class="color-black-1 font-16 font-weight-bold ml-2">
        {{ $t('DocumentsAdditionalToTheInvestigation') }}
      </span>
    </div>
    <div
      v-for="(item, index) in documents"
      :key="`doc-item-${index}`"
      class="row bg-white p-3 rounded mb-2"
    >
      <div class="col-4">
        <div class="color-gray-18 font-14">{{ $t('DocType') }}:</div>
        <div>
          <span class="color-black font-14">
            {{ item.documentCategoryName }}
          </span>
        </div>
      </div>
      <div class="col">
        <div v-if="item.filename">
          <div class="color-gray-18 font-14">{{ $t('Document') }}:</div>
          <span class="color-black font-14">
            {{ item.filename }}
          </span>
        </div>
      </div>
      <div class="col d-flex align-items-center">
        <span
          v-if="!item.documentId && !isReadOnly"
          class="color-red-1 font-14"
        >
          {{ $t('Required') }}*
        </span>
      </div>
      <div class="col d-flex align-items-center justify-content-end">
        <action-button
          v-if="item.documentId && !isReadOnly"
          class="btn-outline-success delete-action mr-2"
          i="fal fa-minus-circle"
          @click="onDeleteHandler(item)"
        />
        <action-button
          v-if="item.documentId"
          class="warning px-4 mr-2 download-action"
          i="fa fa-cloud-download"
          @click="onDownloadHandler(item.documentId)"
        />
      </div>
    </div>
    <div
      v-if="!showAddFile && !isReadOnly"
      class="bg-white rounded p-4 d-flex align-items-center justify-content-center"
    >
      <action-button
        :text="$t('ClickToUploadDocument')"
        class="warning outline-warning px-4 mr-2"
        i="fal fa-cloud-upload"
        @click="onShowFileHandler"
      />
    </div>
    <validation-observer ref="formRef" v-slot="{}" tag="form">
      <div v-if="showAddFile" class="row bg-white rounded p-4 mx-0 mt-2">
        <input
          v-show="false"
          id="fileUpload"
          ref="refFileUpload"
          accept=".pdf"
          type="file"
          @change="updateFileChange($event)"
        />
        <div class="col">
          <validation-provider rules="required" v-slot="{ errors }">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('DocType') | capitalize }}:
            </div>
            <custom-drop-down
              :placeholder="$t('Item-Empty')"
              :options="categories"
              fieldkey="id"
              fieldname="name"
              v-model="model.documentType"
              :error="errors[0]"
              :custom-error-msg="`${$t('RequiredField')}.`"
            />
          </validation-provider>
        </div>
        <div class="col">
          <div class="color-primary-4 font-14 font-weight-bold mb-1">
            {{ $t('SelectDocument') | capitalize }}:
          </div>
          <action-button
            v-if="!selectedFileName"
            :text="$t('ClickToUploadDocument')"
            class="warning outline-warning px-4 mr-2"
            i="fal fa-cloud-upload"
            @click="onAddFileHandler"
          />
          <div v-else class="d-flex align-items-center">
            <div class="color-black font-14 font-weight-normal mr-2">
              {{ selectedFileName }}
            </div>
            <action-button
              variant="outline-success mr-2"
              i="fas fa-file-download pl-1"
              @click="downloadFile"
            />
            <action-button
              variant="outline-danger"
              i="fas fa-times-circle pl-1"
              @click="removeFile"
            />
          </div>
          <validation-provider
            :rules="{ required_if: !model.file }"
            v-slot="{ errors }"
          >
            <custom-error
              v-if="errors[0]"
              class="no-border mt-2"
              :message="`${$t('ShouldSelectDocument')}`"
            />
          </validation-provider>
        </div>
        <div class="col d-flex align-items-center">
          <b-form-checkbox v-model="model.isPublic" class="d-block mt-4">
            <span class="color-black-3 font-14 font-weight-bold">
              {{ $t('IsPublic') }}
            </span>
          </b-form-checkbox>
        </div>
        <div class="col d-flex align-items-center">
          <action-button
            class="btn-warning-outline outline-warning px-4 py-2 mr-3"
            :text="$t('File-Cancel')"
            i="far fa-times-circle"
            @click="onShowFileHandler"
          />
          <action-button
            class="btn-success px-4 py-2"
            :text="$t('FnInkSave')"
            i="fa fa-save"
            @click="onSaveHandler"
          />
        </div>
      </div>
    </validation-observer>
  </div>
</template>

<script>
//libs
import ActionButton from 'ActionButton';
import CustomDropDown from 'custom-drop-down';
import CustomError from 'custom-error';

//components
import { CircleIcon } from '@/components/common';

//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';

//services
import {
  downloadItemAttachment,
  removeItemAttachment,
  saveItemAttachment,
} from '@/services/app.api.js';
import objectToFormData from 'object-to-formdata';
import { i18n } from '@/i18n';

export default {
  name: 'Documents',
  mixins: [alertMixin],
  components: {
    ActionButton,
    CircleIcon,
    CustomDropDown,
    CustomError,
  },
  props: {
    itemId: {
      type: Number,
      required: true,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    documents: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      options: {
        categories: [],
      },
      model: {
        documentType: null,
        file: null,
        isPublic: null,
      },
      fileReader: null,
      showAddFile: false,
    };
  },
  computed: {
    selectedFileName() {
      return this.model?.file?.name;
    },
  },
  methods: {
    async onShowFileHandler() {
      this.showAddFile = !this.showAddFile;
      this.model = {
        documentType: null,
        file: null,
        isPublic: null,
      };

      this.resetFileInput();
    },
    resetFileInput() {
      this.model = {
        ...this.model,
        file: null,
      };
      this.fileReader = null;
    },
    async onDownloadHandler(documentId) {
      if (documentId) {
        await downloadItemAttachment(documentId).catch((error) => {
          this.ShowToast('', error.response.data.message, 'error');
        });
      }
    },
    onAddFileHandler() {
      document.getElementById('fileUpload').click();
    },
    onRefreshHandler() {
      this.$emit('on-refresh-document');
    },
    async updateFileChange(event) {
      if (event.target?.files?.length) {
        const file = this.$el.querySelector('input[type=file]')?.files[0];

        if (file) {
          const reader = new FileReader();

          reader.readAsDataURL(file);

          this.model = {
            ...this.model,
            file,
          };

          this.fileReader = reader;
        }
      }
      event.target.value = '';
    },
    async onSaveHandler() {
      const isValid = await this.$refs.formRef.validate();
      if (!isValid) {
        this.ShowToast(
          i18n.t('Alert'),
          `${i18n.t('PleaseCompleteRequestedInformation')}.`,
          'error'
        );
        return;
      }

      const model = this.model;

      let formData = objectToFormData(
        {
          itemId: this.itemId,
          fileExtension: 'pdf',
          categoryId: model.documentType.id,
          fileName: model.file.name,
          file: model.file,
          isPublic: model.isPublic,
        },
        {
          indices: true,
          nullsAsUndefineds: true,
        }
      );
      console.log(formData);
      console.log(this.model);
      debugger;
      await saveItemAttachment(formData);

      await this.onShowFileHandler();
      this.onRefreshHandler();
    },
    async onDeleteHandler(item) {
      if (item.documentId) {
        const answer = await this.ShowCancelModal(
          'Remover Documento',
          '¿Está seguro que desea remover el documento?',
          'Si',
          'No'
        );
        if (answer) {
          await removeItemAttachment(item.documentId);
          this.ShowToast(
            'Proceso exitoso',
            'El documento ha sido removido exitosamente.',
            'success'
          );
          this.onRefreshHandler();
        }
      }
    },
    removeFile(e) {
      e?.preventDefault();
      this.model = {
        ...this.model,
        file: null,
      };
      this.fileReader = null;
    },
    downloadFile() {
      if (this.fileReader) {
        let a = document.createElement('a');
        document.body.appendChild(a);
        a.style = 'display: none';
        let url = this.fileReader.result;
        a.href = url;
        a.download = this.model.file.name;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/components/action-button/index.scss';
@import '@/scss/bootstrap/_colors.scss';

::v-deep .delete-action {
  border-radius: 4px;
  border: 1px solid #0f556a;
  width: 38px;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    background: unset;
    color: unset;
  }
  i {
    font-size: 20px;
    margin-right: 0 !important;
  }
}

::v-deep .download-action {
  border-radius: 4px;
  width: 38px;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  i {
    font-size: 20px;
    margin-right: 0 !important;
  }
}
</style>
